import { graphql, type ResultOf } from '@teachino/shared-infrastructure';
import {
  convertCourseFragment,
  type Course,
  courseFragment,
} from './course.fragment';

export const topicDetailsFragment = graphql(
  `
    fragment TopicDetails on TopicContent {
      topic {
        id
        name
        goals
        permissions {
          canEditTopic
          canModifyMaterials
          canDeleteTopicFromOwnLibrary
          canDuplicateTopic
        }
        usage
        libraryFolder {
          id
        }
      }
      topicInstances {
        id
        course {
          ...Course
        }
      }
      itemContents {
        item {
          id
          name
          icon
          duration
          contentStatistics {
            imageCount
            fileCount
            linkCount
            noteCount
            interactiveAssignmentCount
          }
          createdAt
        }
        itemInstances {
          id
          course {
            id
          }
          dates
          courseContentVisibility {
            contentVisibility
          }
        }
      }
    }
  `,
  [courseFragment],
);

type ItemWithCourses = {
  id: string;
  name: string;
  icon: string | null;
  duration: number | null;
  courseSettings: {
    id: string;
    isVisible: boolean;
    dates: string[];
  }[];
  contentStatistics: {
    imageCount: number;
    fileCount: number;
    linkCount: number;
    noteCount: number;
  } | null;
  createdAt: Date;
};

export type TopicDetails = {
  items: ItemWithCourses[];
  courses: Course[];
  id: string;
  name: string;
  goals: string | null;
  permissions: {
    canEditTopic: boolean;
    canModifyMaterials: boolean;
    canDeleteTopicFromOwnLibrary: boolean;
    canDuplicateTopic: boolean;
  };
  folder: {
    id: string;
  } | null;
  usage: ResultOf<typeof topicDetailsFragment>['topic']['usage'];
};

export function convertTopicDetails(
  topicDetails: ResultOf<typeof topicDetailsFragment>,
): TopicDetails {
  const courses: Course[] = topicDetails.topicInstances.map((topicInstance) =>
    convertCourseFragment(topicInstance.course),
  );

  const items: ItemWithCourses[] = topicDetails.itemContents.map(
    (itemContent) => {
      return {
        ...itemContent.item,
        courseSettings: itemContent.itemInstances.map((itemInstance) => {
          const course = itemInstance.course;
          if (!course) {
            throw new Error('course not defined');
          }
          return {
            id: course.id,
            dates: itemInstance.dates,
            isVisible:
              itemInstance.courseContentVisibility?.contentVisibility ===
              'VISIBLE',
          };
        }),
        createdAt: new Date(itemContent.item.createdAt),
      };
    },
  );

  const { id, name, goals, usage } = topicDetails.topic;
  const folder = topicDetails.topic.libraryFolder?.id
    ? {
        id: topicDetails.topic.libraryFolder.id,
      }
    : null;
  const permissions = topicDetails.topic.permissions;
  if (!permissions) {
    throw new Error(
      `Received TopicDetails without Permissions. ${JSON.stringify(topicDetails)}`,
    );
  }
  return {
    id,
    name,
    goals,
    permissions,
    courses,
    items,
    folder,
    usage,
  };
}
